import type { HelpContent } from './types';

export const settingsProjectHelp: HelpContent = {
  pageTitle: 'Project Settings',
  description: 'Project Settings is where administrators define how projects are created, structured, and tracked — including status labels, custom fields, budget thresholds, and billing configuration.',

  functionality: {
    overview:
      'This page contains multiple tabbed sections covering every aspect of project configuration. Settings here apply company-wide to all projects and all users. Changes take effect immediately once saved.',
    keyFeatures: [
      {
        label: 'Project Creation Rules',
        detail: 'Control which fields are required when creating a project, and set default values for new projects.',
        requiredPermission: 'settings.projectSettings',
      },
      {
        label: 'Status Labels',
        detail: 'Customise the status labels available on projects — such as Active, On Hold, or Completed — and configure their colours.',
        requiredPermission: 'settings.projectSettings',
      },
      {
        label: 'Custom Fields',
        detail: 'Add up to the allowed maximum of custom fields to capture project metadata unique to your business.',
        requiredPermission: 'settings.projectSettings',
      },
      {
        label: 'Budget Health Thresholds',
        detail: 'Define the percentage thresholds at which a budget status changes from healthy to warning to critical.',
        requiredPermission: 'settings.projectSettings',
      },
      {
        label: 'Billing Configuration',
        detail: 'Set default billing terms, invoice schedules, and write-off reason codes.',
        requiredPermission: 'settings.projectSettings',
      },
      {
        label: 'Project Roles',
        detail: 'Configure which project roles are available (e.g. Project Manager, Sponsor) and their display order.',
        requiredPermission: 'settings.projectSettings',
      },
      {
        label: 'Cost Categories',
        detail: 'Manage the categories used to classify project costs, and map them to nominal codes in your finance system.',
        requiredPermission: 'settings.projectSettings',
      },
    ],
    sectionsAndFields: [
      {
        name: 'Project Creation tab',
        description: 'Fields and toggles that control what information is captured when a new project is created.',
        requiredPermission: 'settings.projectSettings',
      },
      {
        name: 'Status Labels',
        description: 'List of configurable project status options — each with a name, colour indicator, and active/inactive toggle.',
        requiredPermission: 'settings.projectSettings',
      },
      {
        name: 'Custom Fields',
        description: 'A list of custom metadata fields that appear on project records. Each field has a name, type, and required flag.',
        requiredPermission: 'settings.projectSettings',
      },
      {
        name: 'Budget Thresholds',
        description: 'Slider or numeric inputs defining the warning and critical budget consumption percentages for health indicators.',
        requiredPermission: 'settings.projectSettings',
      },
      {
        name: 'Write-Off Reasons',
        description: 'A list of reason codes users can select when writing off budget or WIP amounts.',
        requiredPermission: 'settings.projectSettings',
      },
      {
        name: 'Project Roles',
        description: 'Toggle and order the key people roles available on projects, such as Project Manager, Sponsor, and Account Director.',
        requiredPermission: 'settings.projectSettings',
      },
      {
        name: 'Cost Categories',
        description: 'Manage the cost classification categories and their mapping to nominal codes for finance system integration.',
        requiredPermission: 'settings.projectSettings',
      },
      {
        name: 'Billing & Invoicing',
        description: 'Default billing terms, invoice prefix, and schedule configuration for new projects.',
        requiredPermission: 'settings.projectSettings',
      },
    ],
    useCases: [
      'An administrator adds a custom field to capture a client purchase order number on every project.',
      'A team lead adjusts budget health thresholds so the warning indicator triggers at 80% instead of 75%.',
      'An administrator adds a new write-off reason code required by the finance team.',
      'A PM wants to enable a project status label called "Pending Approval" with a yellow colour indicator.',
    ],
  },

  userGuide: {
    gettingStarted:
      'Navigate to the tab that covers the area you want to configure. Most sections list existing items that can be edited or deleted, plus an option to add new ones. Click Save (or the section-level save button) to commit changes.',
    steps: [
      {
        title: 'How do I add a new project status label?',
        requiredPermission: 'settings.projectSettings',
        items: [
          { text: 'Click the Status Labels tab or section.' },
          { text: 'Click Add Status to open the creation form.' },
          { text: 'Enter a label name and select a colour.' },
          {
            text: 'Toggle the status Active so it appears as an option on projects.',
            note: 'Inactive statuses are hidden from the project status dropdown but are not deleted — existing projects that use the status are not affected.',
          },
          { text: 'Click Save to add the status.' },
        ],
      },
      {
        title: 'How do I add a custom field?',
        requiredPermission: 'settings.projectSettings',
        items: [
          { text: 'Click the Custom Fields section.' },
          { text: 'Click Add Field.' },
          { text: 'Enter a field name and select the field type (Text, Number, Date, or Dropdown).' },
          {
            text: 'Tick Required if the field must be filled in when creating a project.',
            note: 'There is a maximum number of custom fields per account. If you have reached the limit the Add Field button will be disabled.',
          },
          { text: 'Click Save.' },
        ],
      },
      {
        title: 'How do I change the budget health thresholds?',
        requiredPermission: 'settings.projectSettings',
        items: [
          { text: 'Open the Budget Health Thresholds section.' },
          {
            text: 'Adjust the warning and critical percentage values using the sliders or by typing directly into the fields.',
            tip: 'A common setup is Warning at 75% and Critical at 90% of budget consumed.',
          },
          { text: 'Click Save to apply the change to all projects.' },
        ],
      },
      {
        title: 'How do I manage write-off reason codes?',
        requiredPermission: 'settings.projectSettings',
        items: [
          { text: 'Open the Write-Off Reasons section.' },
          { text: 'Click Add Reason to create a new code.' },
          { text: 'Enter the reason description and click Save.' },
          {
            text: 'To remove a reason, click the delete icon next to it.',
            note: 'Reason codes that are already used on existing write-off records cannot be deleted. Deactivate them instead to hide them from future selections.',
          },
        ],
      },
    ],
    proTips: [
      'Status labels are shared across all projects. Renaming a label updates it everywhere immediately.',
      'Use custom fields sparingly — they appear on every project record. Too many fields create clutter on project creation forms.',
      'Budget health thresholds affect the colour indicators shown on project cards and in the portfolio view. Adjust them to reflect your team\'s risk tolerance.',
      'Write-off reason codes are also visible in the audit log, so use descriptive names that will make sense in a compliance review.',
    ],
  },

  faq: [
    {
      question: 'Can I delete a project status label that is already in use?',
      answer:
        'Deleting a status label that is assigned to existing projects is blocked. Deactivate the label instead — it will be hidden from the status dropdown on new projects but existing records are not affected.',
      requiredPermission: 'settings.projectSettings',
    },
    {
      question: 'How many custom fields can I add?',
      answer:
        'The maximum number of custom fields is shown in the Custom Fields section. Once the limit is reached the Add Field button is disabled.',
      requiredPermission: 'settings.projectSettings',
    },
    {
      question: 'What does the budget health threshold control?',
      answer:
        'It sets the percentage of budget consumed at which the project health indicator changes from green (healthy) to amber (warning) to red (critical). These indicators appear on project cards, the portfolio view, and dashboard widgets.',
      requiredPermission: 'settings.projectSettings',
    },
    {
      question: 'Can I reorder project role labels?',
      answer:
        'Yes. In the Project Roles section you can drag roles up or down to change the display order on project key people panels.',
      requiredPermission: 'settings.projectSettings',
    },
    {
      question: 'What are cost categories used for?',
      answer:
        'Cost categories classify project expenses and time entries. They are used in the project cost budget, P&L Report breakdowns, and can be mapped to nominal codes in your finance system for accurate posting.',
      requiredPermission: 'settings.projectSettings',
    },
  ],

  relatedPages: [
    { label: 'General Settings', path: '/settings/general' },
    { label: 'Integrations', path: '/settings/integrations' },
    { label: 'Roles & Permissions', path: '/settings/user-permissions' },
    { label: 'Projects', path: '/projects' },
  ],
};
